import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';

const Home = () => {
  const [settings, setSettings] = useState({});
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, menuRes] = await Promise.all([
          api.get('/api/settings'),
          api.get('/api/menu')
        ]);
        setSettings(settingsRes.data);
        setMenuItems(menuRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="bg-ink">

      {/* ═══════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <img
          src="https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1800&q=90"
          alt="Tastywood ambiance"
          className="absolute inset-0 w-full h-full object-cover scale-105"
          style={{ animation: 'heroZoom 20s ease-in-out infinite alternate' }}
        />
        <style>{`
          @keyframes heroZoom { from { transform: scale(1.05); } to { transform: scale(1.12); } }
        `}</style>

        {/* Overlay */}
        <div className="hero-overlay absolute inset-0"></div>

        {/* Chinese Lattice Accent */}
        <div className="lattice-pattern absolute inset-0 opacity-[0.04]"></div>

        {/* Vertical text accent */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3">
          <div className="h-20 w-px bg-warm-gold/30"></div>
          <span className="text-warm-gold/50 text-xs tracking-[0.5em] uppercase" style={{writingMode:'vertical-rl'}}>Since 2020</span>
          <div className="h-20 w-px bg-warm-gold/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 w-full">
          <div className="max-w-4xl">
            {/* Pre-line */}
            <div className="flex items-center gap-4 mb-10">
              <div className="h-px w-14 bg-warm-gold"></div>
              <span className="text-warm-gold text-xs tracking-[0.45em] uppercase">Toronto · Canada · Est. 2020</span>
            </div>

            {/* Headline */}
            <h1 className="font-serif leading-none mb-8">
              <span className="block text-white text-6xl md:text-8xl mb-2">Taste the</span>
              <span className="block text-7xl md:text-9xl text-gradient-gold">Dynasty.</span>
            </h1>

            <p className="text-white/50 text-lg md:text-xl max-w-lg mb-14 leading-relaxed font-light">
              {settings.announcement || 'Where centuries of Chinese culinary artistry meet the bold spirit of modern Canada.'}
            </p>

            <div className="flex flex-wrap gap-5">
              <Link
                to="/menu"
                className="group inline-flex items-center gap-3 px-10 py-4 bg-imperial-red text-white text-xs tracking-[0.25em] uppercase font-semibold hover:bg-warm-gold hover:text-ink transition-all duration-300"
              >
                Explore Menu
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-10 py-4 border border-white/25 text-white text-xs tracking-[0.25em] uppercase font-semibold hover:border-warm-gold hover:text-warm-gold transition-all duration-300"
              >
                Reserve a Table
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-white/30 text-xs tracking-[0.4em] uppercase">Scroll</span>
          <div className="w-px h-16 bg-warm-gold/30 origin-top animate-scroll-line"></div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          ANNOUNCEMENT BAR
      ═══════════════════════════════════════════════ */}
      <div className="bg-imperial-red py-3.5">
        <p className="text-center text-white text-xs tracking-[0.35em] uppercase font-medium">
          🏮 &nbsp; {settings.announcement || 'Welcome to King\'s Noodles — Now Open Daily 11AM–10PM'} &nbsp; 🏮
        </p>
      </div>

      {/* ═══════════════════════════════════════════════
          ABOUT
      ═══════════════════════════════════════════════ */}
      <section className="py-36 bg-ink relative overflow-hidden">
        {/* Decorative watermark character */}
        <div
          className="absolute -right-16 top-1/2 -translate-y-1/2 font-serif text-white select-none pointer-events-none leading-none"
          style={{ fontSize: '32rem', opacity: 0.018, userSelect: 'none' }}
        >龍</div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-24 items-center">

            {/* Image composition */}
            <div className="relative">
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=700&q=90"
                  alt="Restaurant interior"
                  className="w-full h-[540px] object-cover"
                />
                {/* Gold border overlay */}
                <div className="absolute inset-0 border border-warm-gold/20 pointer-events-none"></div>
              </div>
              {/* Corner decoration */}
              <div className="absolute -bottom-8 -right-8 w-52 h-52 border border-warm-gold/20"></div>
              <div className="absolute -top-8 -left-8 w-52 h-52 border border-imperial-red/15"></div>

              {/* Floating stat card */}
              <div className="absolute -bottom-6 left-6 glass-card px-6 py-5 border-l-2 border-warm-gold">
                <div className="font-serif text-4xl text-warm-gold font-bold">15+</div>
                <div className="text-white/50 text-xs tracking-[0.3em] uppercase mt-1">Years of Excellence</div>
              </div>
            </div>

            {/* Text */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px w-8 bg-warm-gold"></div>
                <span className="text-warm-gold text-xs tracking-[0.4em] uppercase">Our Heritage</span>
              </div>
              <h2 className="font-serif text-5xl text-white mb-8 leading-tight">
                Where Tradition<br />
                <span className="text-gradient-gold">Meets Innovation</span>
              </h2>
              <p className="text-white/45 leading-relaxed mb-5 font-light">
                Tastywood was born from a vision to honour the centuries-old culinary heritage of China while embracing the bold, multicultural spirit of Canada. Every dish is a bridge between two worlds.
              </p>
              <p className="text-white/45 leading-relaxed mb-10 font-light">
                Our chefs trained in Beijing and Shanghai bring authentic techniques to your table — using locally-sourced Canadian ingredients to create a dining experience unlike any other.
              </p>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-6 mb-12 pt-8 border-t border-white/8">
                {[
                  { num: '80+',  label: 'Signature Dishes' },
                  { num: '15K+', label: 'Happy Guests'     },
                  { num: '12',   label: 'Awards Won'       },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-serif text-3xl text-warm-gold font-bold">{s.num}</div>
                    <div className="text-white/35 text-xs tracking-[0.25em] uppercase mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              <Link
                to="/menu"
                className="group inline-flex items-center gap-3 text-warm-gold text-xs tracking-[0.25em] uppercase font-semibold hover:gap-5 transition-all duration-300"
              >
                <span>Discover Our Menu</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section divider */}
      <div className="section-divider">
        <div className="section-divider-icon"><span>✦</span></div>
      </div>

      {/* ═══════════════════════════════════════════════
          FEATURED DISHES
      ═══════════════════════════════════════════════ */}
      <section className="py-36 bg-ink-light relative">
        <div className="max-w-7xl mx-auto px-6">

          {/* Section header */}
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-4 mb-5">
              <div className="h-px w-16 bg-warm-gold/30"></div>
              <span className="text-warm-gold text-xs tracking-[0.45em] uppercase">Chef's Selection</span>
              <div className="h-px w-16 bg-warm-gold/30"></div>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl text-white mb-4">
              Featured <span className="text-gradient-gold">Dishes</span>
            </h2>
            <p className="text-white/35 text-sm max-w-sm mx-auto leading-relaxed">
              Curated daily from the finest seasonal ingredients by our head chef.
            </p>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-7">
            {menuItems.map((item) => (
              <div key={item.id} className="dish-card group relative overflow-hidden">
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent"></div>
                  {/* Category badge */}
                  <div className="absolute top-4 left-4 bg-imperial-red text-white text-xs px-3 py-1 tracking-[0.2em] uppercase">
                    {item.category}
                  </div>
                  {/* Gold line on hover */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-warm-gold transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>

                {/* Body */}
                <div className="p-6 border-t border-white/5">
                  <h3 className="font-serif text-xl text-white mb-2">{item.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed mb-5 line-clamp-2">{item.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-2xl text-warm-gold">${item.price}</span>
                    <button className="text-white/30 hover:text-warm-gold text-xs tracking-[0.2em] uppercase transition-colors duration-300">
                      Details →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/menu"
              className="inline-flex items-center gap-4 px-12 py-4 border border-warm-gold/40 text-warm-gold text-xs tracking-[0.3em] uppercase font-semibold hover:bg-warm-gold hover:text-ink transition-all duration-300"
            >
              View Full Menu
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          EXPERIENCE BANNER
      ═══════════════════════════════════════════════ */}
      <section className="relative py-44 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1601050690597-df0568f70950?w=1800&q=85"
          alt="Dining experience"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{background:'rgba(8,8,8,0.78)'}}></div>
        <div className="lattice-pattern absolute inset-0 opacity-[0.04]"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="text-5xl mb-6 animate-float inline-block">🏮</div>
            <h2 className="font-serif text-5xl md:text-6xl text-white mb-6 leading-tight">
              An Unforgettable<br />
              <span className="text-gradient-gold">Dining Experience</span>
            </h2>
            <p className="text-white/45 text-lg mb-14 leading-relaxed font-light max-w-lg mx-auto">
              From the moment you enter, every detail — the lighting, the music, the aromas — is crafted to transport you to the heart of imperial China.
            </p>
            <Link
              to="/contact"
              className="inline-block px-14 py-5 bg-imperial-red text-white text-xs tracking-[0.3em] uppercase font-semibold hover:bg-warm-gold hover:text-ink transition-all duration-300"
            >
              Book Your Table
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          TESTIMONIALS
      ═══════════════════════════════════════════════ */}
      <section className="py-36 bg-ink">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-5">
              <div className="h-px w-16 bg-warm-gold/30"></div>
              <span className="text-warm-gold text-xs tracking-[0.45em] uppercase">Guest Stories</span>
              <div className="h-px w-16 bg-warm-gold/30"></div>
            </div>
            <h2 className="font-serif text-5xl text-white">
              What Guests <span className="text-gradient-gold">Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-7">
            {[
              { quote: 'The Peking Duck here rivals anything I\'ve had in Beijing. Absolutely transcendent.', author: 'Sophie M.', city: 'Toronto' },
              { quote: 'The atmosphere is magical — dim lighting, gorgeous décor, and food that tells a story.', author: 'James T.', city: 'Vancouver' },
              { quote: 'A culinary journey through Chinese history. The Ginger Beef is a must-order.', author: 'Priya K.', city: 'Ottawa' },
            ].map((t, i) => (
              <div key={i} className="glass-card p-8 relative group hover:border-warm-gold/20 transition-colors duration-300">
                {/* Decorative quote mark */}
                <div className="font-serif text-6xl text-warm-gold/25 leading-none mb-4 select-none">"</div>
                <p className="text-white/55 leading-relaxed mb-8 italic font-light">{t.quote}</p>
                {/* Stars */}
                <div className="flex gap-0.5 mb-6">
                  {[...Array(5)].map((_, s) => (
                    <span key={s} className="text-warm-gold text-sm">★</span>
                  ))}
                </div>
                <div className="flex items-center gap-3 pt-5 border-t border-white/6">
                  <div className="w-10 h-10 bg-imperial-red/20 border border-imperial-red/30 flex items-center justify-center text-warm-gold font-serif font-bold text-sm">
                    {t.author[0]}
                  </div>
                  <div>
                    <div className="text-white text-sm font-semibold">{t.author}</div>
                    <div className="text-white/35 text-xs tracking-[0.25em] uppercase">{t.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
